import { OutsideClick } from '@cian/outside-click-component';
import { mergeStyles } from '@cian/utils';
import * as React from 'react';

import { Button } from '../../../common/components/button';
import { IOffer } from '../../../types/offer';
import { Comment } from './comment';

const style = require('./index.css');

export interface ICommentsAPI {
  onOfferCommentsChanged(
    offer: IOffer,
    commentOffer: string | undefined,
    commentAgent: string | undefined,
  ): void;

  isAuthenticated: boolean;
  commentingBlockedOffers: number[];
  commentingBlockedAgents: number[];
  commentingErroneousOffers: number[];
  commentingErroneousAgents: number[];
}

export interface ICommentsProps extends ICommentsAPI {
  isSimplified?: boolean;
  offer: IOffer;
  onCompletion(): void;
}

interface ICommentsState {
  isEditing: boolean;
  commentOffer?: string;
  commentAgent?: string;
}

export class Comments extends React.Component<ICommentsProps, ICommentsState> {

  private commentOffer: Comment | null = null;

  public constructor(props: ICommentsProps) {
    super(props);

    this.state = {
      commentAgent: props.offer.notes.realtor && props.offer.notes.realtor.text || undefined,
      commentOffer: props.offer.notes.offer && props.offer.notes.offer.text || undefined,
      isEditing: false,
    };
  }

  public componentDidMount() {
    if (!this.state.commentOffer && !this.state.commentAgent && this.commentOffer) {
      const textarea = this.commentOffer.getTextfield();
      if (textarea) {
        textarea.focus();
      }
    }
  }

  public componentWillReceiveProps(nextProps: ICommentsProps) {
    const { offer } = this.props;

    // tslint:disable-next-line:no-any
    let nextState: any = {};

    if (this.props.offer !== nextProps.offer) {
      nextState = Object.assign(nextState, {
        commentAgent: nextProps.offer.notes.realtor && nextProps.offer.notes.realtor.text || undefined,
        commentOffer: nextProps.offer.notes.offer && nextProps.offer.notes.offer.text || undefined,
      });
    }

    if (this.state.isEditing) {
      if (this.props.commentingBlockedOffers.indexOf(offer.cianId || offer.id) > -1 &&
        nextProps.commentingBlockedOffers.indexOf(offer.cianId || offer.id) === -1 ||
        offer.user && this.props.commentingBlockedAgents.indexOf(offer.user.cianUserId as number) > -1 &&
        nextProps.commentingBlockedAgents.indexOf(offer.user.cianUserId as number) === -1
      ) {
        nextState = Object.assign(nextState, {
          isEditing: false,
        });
      }
    }

    if (Object.keys(nextState).length) {
      this.setState(nextState, () => {
        if (typeof nextState.isEditing !== 'undefined') {
          this.props.onCompletion();
        }
      });
    }
  }

  public render() {
    const {
      offer,
      isAuthenticated,
      commentingBlockedOffers,
      commentingBlockedAgents,
      isSimplified,
    } = this.props;
    const { isEditing, commentOffer, commentAgent } = this.state;

    const isLoading = this.isLoading();
    const isForceShown = isEditing || (!commentOffer && !commentAgent);

    /**
     * Если нет комментариев от пользователя,
     * или есть комментарий от агента, но пользователь не авторизован -
     * не выводим блок
     */
    if (!commentOffer && !commentAgent && !isForceShown) {
      return null;
    }

    return (
      <OutsideClick
        onOutsideClick={this.cancel}
        rootElement={
          <div
            {...mergeStyles(
              style['container'],
              offer.isTop3 && style['container--top3'],
              offer.isColorized && style['container--colorized'],
              isSimplified && style['simplified'],
              isEditing && style['simplified-editing'],
            )}
          />
        }
      >
        <div className={style['title']}>Комментарий</div>
        <div
          {...mergeStyles(
            style['content'],
            isEditing && style['content--editing'],
          )}
        >
          {(commentOffer || isForceShown) && <Comment
            ref={(commentOfferRef) => this.commentOffer = commentOfferRef}
            isSimplified
            label="Про объявление"
            value={commentOffer}
            editing={isEditing}
            disabled={commentingBlockedOffers.indexOf(offer.cianId || offer.id) > -1}
            onDelete={() => {
              this.setState({
                commentOffer: undefined,
              }, this.handleSave);
            }}
            onChange={(value) => this.setState({
              commentOffer: value,
            })}
            onKeyDown={this.handleKeyDown}
            onFocus={this.handleFocus} />}
          {isAuthenticated && (commentAgent || isForceShown) && <Comment
            isSimplified
            label="Про агента"
            value={commentAgent}
            editing={isEditing}
            disabled={offer.user && commentingBlockedAgents.indexOf(offer.user.cianUserId as number) > -1 || false}
            onDelete={() => {
              this.setState({
                commentAgent: undefined,
              }, this.handleSave);
            }}
            onChange={(value) => this.setState({
              commentAgent: value,
            })}
            onKeyDown={this.handleKeyDown}
            onFocus={this.handleFocus} />}
        </div>
        {isEditing && <Button
          className={style['save']}
          theme="primary"
          mode="light"
          onClick={this.handleSave}
          disabled={isLoading}
        >Сохранить</Button>}
      </OutsideClick>
    );
  }

  private isLoading() {
    const { offer, commentingBlockedOffers, commentingBlockedAgents } = this.props;

    if (commentingBlockedOffers.indexOf(offer.cianId || offer.id) > -1 ||
      offer.user && commentingBlockedAgents.indexOf(offer.user.cianUserId as number) > -1
    ) {
      return true;
    }

    return false;
  }

  private handleFocus = () => {
    this.setState({
      isEditing: true,
    });
  }

  private handleKeyDown = (e: React.KeyboardEvent<EventTarget>) => {
    // Копипаста из @cian/components
    // tslint:disable-next-line:no-any
    const code = e.nativeEvent.code || e.nativeEvent.key || (e.nativeEvent as any).keyIdentifier;

    if (code === 'Enter') {
      this.handleSave();
    }
  }

  private cancel = () => {
    if (this.isLoading()) {
      return;
    }

    const { notes } = this.props.offer;

    this.setState({
      commentAgent: notes.realtor && notes.realtor.text || undefined,
      commentOffer: notes.offer && notes.offer.text || undefined,
      isEditing: false,
    });

    this.props.onCompletion();
  }

  private handleSave = () => {
    if (this.isLoading()) {
      return;
    }

    this.props.onOfferCommentsChanged(this.props.offer, this.state.commentOffer, this.state.commentAgent);
  }
}
